import { createContext, useContext, useEffect, useState } from 'react';

import type { GlobalApiError } from '@data/global-api-errors';
import { useTranslationsContext } from '@state/checkout-contexts';

import { useHandleCommonErrors } from '../../hooks/useHandleCommonErrors/useHandleCommonErrors';
import { getApiErrorCodeAndMessageKey } from '../../utils/getApiErrorCodeAndMessageKey';
import { getTraceIdAndTimestamp } from '../../utils/getTraceIdAndTimestamp';

import type {
  ErrorContextData,
  ErrorContextProviderProps,
} from './errorContext.types';

const ErrorContext = createContext<ErrorContextData | null>(null);

const useErrorContext = () => {
  const context = useContext(ErrorContext);

  if (!context) {
    throw new Error(
      'useErrorContext hook must be used within a <ErrorContextProvider>'
    );
  }

  return context;
};

const ErrorContextProvider = ({ children }: ErrorContextProviderProps) => {
  const { t } = useTranslationsContext();
  const [error, setError] = useState<GlobalApiError | null>(null);
  const { handleError } = useHandleCommonErrors();
  const { messageKey, code, rawMessage } = getApiErrorCodeAndMessageKey(error);
  const errorMessage =
    messageKey && rawMessage
      ? `${t(messageKey)}${getTraceIdAndTimestamp(rawMessage)}`
      : null;

  useEffect(() => {
    handleError(code, errorMessage);
  }, [code, errorMessage, handleError, error]);

  return (
    <ErrorContext.Provider
      value={{
        errorMessage,
        errorCode: code,
        error,
        setError,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

const withErrorContextProvider = ({ children }: ErrorContextProviderProps) => (
  <ErrorContextProvider>{children}</ErrorContextProvider>
);

export {
  ErrorContext,
  ErrorContextProvider,
  useErrorContext,
  withErrorContextProvider,
};
